<template>
  <div class="flex flex-col gap-2">
    <template v-if="mode === 'view'">
      <div>
        <ViewModeLabel>{{ label }}</ViewModeLabel>
        <ViewModeValue>{{ typeDisplay }}</ViewModeValue>
        <div v-if="showUserList" class="flex flex-wrap gap-1 mt-1">
          <Badge
            v-for="userName in selectedUserNames"
            :key="userName"
            variant="secondary"
            :capitalize="shouldCapitalize(userName)"
          >
            {{ userName }}
          </Badge>
          <span v-if="selectedUserNames.length === 0" class="text-sm text-muted-foreground"> No users selected </span>
        </div>
      </div>
    </template>
    <template v-else>
      <FormKit
        :label="label"
        :name="name"
        type="select"
        :options="typeOptions"
        :model-value="selectedType"
        outer-class="w-48 max-w-48 !m-0"
        @update:model-value="handleTypeChange"
      />
      <div v-if="showUserList" class="flex flex-col gap-1 max-h-40 overflow-y-auto border rounded-md p-2 bg-background">
        <div v-if="isLoading" class="text-sm text-muted-foreground">Loading users...</div>
        <div v-else-if="activeTeamMembers.length === 0" class="text-sm text-muted-foreground">
          No team members found
        </div>
        <label
          v-for="member in sortedTeamMembers"
          v-else
          :key="member.id"
          :class="[
            'flex items-center gap-2 p-1.5 rounded-md transition-colors',
            isUserDisabled(member) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-accent',
          ]"
        >
          <input
            type="checkbox"
            :checked="selectedUserIds.includes(member.id)"
            :disabled="isUserDisabled(member)"
            class="h-4 w-4 rounded border-input"
            @change="handleToggleUser(member)"
          />
          <span class="text-sm font-medium">{{ member.name || member.email }}</span>
          <span v-if="member.name" class="text-xs text-muted-foreground">({{ member.email }})</span>
          <Badge
            v-if="showPhoneWarning && !member.mobile_phone_number"
            variant="outline"
            class="text-amber-600 border-amber-300"
          >
            no phone
          </Badge>
        </label>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { toRef } from 'vue';
import { Badge } from '@/components/ui/badge';
import { useTeamStore } from '@/stores/team';
import { useUserSelector, type UserSelectorOption } from './useUserSelector';
import type { ActionItemMode } from '../../types';
import ViewModeValue from '../../../common/ViewModeValue.vue';
import ViewModeLabel from '../../../common/ViewModeLabel.vue';
import type { TeamMemberEntity } from '@/api/entity_types/TeamMember';

type Props = {
  label: string;
  name: string;
  selectedType: string;
  selectedUserIds: number[];
  typeOptions: UserSelectorOption[];
  showUsersForType: string;
  mode: ActionItemMode;
  showPhoneWarning?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  showPhoneWarning: false,
});

type Emits = {
  'update:selectedType': [val: string];
  'update:selectedUserIds': [val: number[]];
};

const emit = defineEmits<Emits>();

const teamStore = useTeamStore();
const activeTeamMembers = toRef(teamStore, 'activeTeamMembers');
const isLoading = toRef(teamStore, 'isLoading');

const { typeDisplay, selectedUserNames, sortedTeamMembers, showUserList, handleTypeChange, toggleUser } =
  useUserSelector({
    selectedType: toRef(props, 'selectedType'),
    selectedUserIds: toRef(props, 'selectedUserIds'),
    teamMembers: activeTeamMembers,
    typeOptions: props.typeOptions,
    showUsersForType: props.showUsersForType,
    onTypeChange: val => emit('update:selectedType', val),
    onUserIdsChange: val => emit('update:selectedUserIds', val),
  });

const shouldCapitalize = (name: string) => {
  const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return !EMAIL_REGEX.test(name);
};

const isUserDisabled = (member: TeamMemberEntity) => {
  return props.showPhoneWarning && !member.mobile_phone_number;
};

const handleToggleUser = (member: TeamMemberEntity) => {
  if (isUserDisabled(member)) return;
  toggleUser(member.id);
};
</script>
